import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Canvas, useFrame, useLoader, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls as ThreeOrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';

/**
 * 轻量电缸 3D 预览器 —— 供选型结果卡片的“查看 3D”弹窗复用。
 *
 * 从 ModelLibraryPanel 提炼核心（加载 GLB + 自动 fit 相机 + OrbitControls），
 * 去掉了试验性的零件点选 / 包围盒统计；ModelLibraryPanel 保持不动。
 * 仅在弹窗打开时挂载，从而懒加载 GLB、关闭即释放。
 */

function Model({ url, onReady }: { url: string; onReady: (root: THREE.Object3D) => void }) {
  const gltf = useLoader(GLTFLoader, url, (loader) => loader.setMeshoptDecoder(MeshoptDecoder));
  useEffect(() => {
    onReady(gltf.scene);
  }, [gltf.scene, onReady]);
  return <primitive object={gltf.scene} />;
}

/** OrbitControls + 模型加载完自动 fit camera（与 ModelLibraryPanel 同源） */
function CameraRig({ target }: { target: THREE.Object3D | null }) {
  const { camera, gl } = useThree();
  const controlsRef = useRef<ThreeOrbitControls | null>(null);

  useEffect(() => {
    const controls = new ThreeOrbitControls(camera, gl.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controlsRef.current = controls;
    return () => {
      controls.dispose();
      controlsRef.current = null;
    };
  }, [camera, gl]);

  useFrame(() => controlsRef.current?.update());

  useEffect(() => {
    if (!target) return;
    const box = new THREE.Box3().setFromObject(target);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;

    if (camera instanceof THREE.PerspectiveCamera) {
      const fovRad = camera.fov * (Math.PI / 180);
      const dist = ((maxDim / 2) / Math.tan(fovRad / 2)) * 1.6;
      camera.position.set(center.x + dist, center.y + dist * 0.6, center.z + dist);
      camera.near = Math.max(dist / 1000, 0.01);
      camera.far = dist * 100;
      camera.updateProjectionMatrix();
      camera.lookAt(center);
    }

    if (controlsRef.current) {
      controlsRef.current.target.copy(center);
      controlsRef.current.minDistance = maxDim * 0.3;
      controlsRef.current.maxDistance = maxDim * 8;
      controlsRef.current.update();
    }
  }, [target, camera]);

  return null;
}

function Lights() {
  return (
    <>
      <ambientLight intensity={0.7} />
      <directionalLight position={[5, 10, 7]} intensity={0.9} />
      <directionalLight position={[-5, -3, -5]} intensity={0.3} />
    </>
  );
}

export default function CylinderModelViewer({ url, height = 420 }: { url: string; height?: number }) {
  const [target, setTarget] = useState<THREE.Object3D | null>(null);
  const handleReady = useCallback((root: THREE.Object3D) => setTarget(root), []);

  return (
    <div
      style={{
        width: '100%',
        height,
        background: '#f6f8fa',
        borderRadius: 8,
        border: '1px solid #eaecef',
      }}
    >
      <Canvas
        key={url}
        camera={{ position: [100, 50, 100], fov: 40, near: 0.1, far: 10000 }}
        gl={{ antialias: true }}
      >
        <Lights />
        <Suspense fallback={null}>
          <Model url={url} onReady={handleReady} />
        </Suspense>
        <CameraRig target={target} />
      </Canvas>
    </div>
  );
}
